// Cookie -> Redis migration (Phase 1) — client-side read helpers.
// The SPA reads device/session data from window.__BRIJ_CTX__, which
// consumer-app-microservice injects during SSR. Call sites use these getters
// first and fall back to their original cookie, so behavior is unchanged when
// the context is absent (Redis down, or the page served outside CA-ms such as
// the local Vite dev server).
//
// Types mirror @brij-it/shared-resources; consolidate to the shared import once
// that package ships them.

interface IpGeo {
  country_code?: string;
  continent_code?: string;
  region_code?: string;
  [key: string]: unknown;
}

interface BrijSession {
  sid?: string;
  did?: string;
  rebate_ids?: Record<string, string>;
  ip_geo?: IpGeo;
  [key: string]: unknown;
}

interface BrijDevice {
  did?: string;
  visit_count?: number;
  [key: string]: unknown;
}

export interface BrijContext {
  device: BrijDevice | null;
  session: BrijSession | null;
}

// Verbose per-read logging is opt-in for staging debugging: add ?brijDebug=1 to
// the URL or run localStorage.setItem('brijDebug','1'). The one-line source
// summary below is always logged once per page load so Redis-vs-cookie is
// visible without any setup.
const brijDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return (
      new URLSearchParams(window.location.search).get('brijDebug') === '1' ||
      window.localStorage.getItem('brijDebug') === '1'
    );
  } catch (e) {
    return false;
  }
};

let bootSummaryLogged = false;

export const readBrijContext = (): BrijContext | undefined => {
  if (typeof window === 'undefined') return undefined;
  const ctx = (window as unknown as { __BRIJ_CTX__?: BrijContext })
    .__BRIJ_CTX__;

  // One concise line per page load so staging can confirm whether the app is
  // actually reading from Redis (SSR-injected) or falling back to cookies.
  if (!bootSummaryLogged) {
    bootSummaryLogged = true;
    if (ctx && typeof ctx === 'object' && (ctx.session || ctx.device)) {
      console.log(
        '[BRIJ_CTX] source=REDIS did=%s sid=%s ip_geo=%s visits=%s is_return=%s',
        ctx.device?.did || ctx.session?.did || '(none)',
        ctx.session?.sid || '(none)',
        ctx.session?.ip_geo ? 'present' : 'absent',
        ctx.device?.visit_count ?? '(n/a)',
        ctx.session?.is_return ?? '(n/a)'
      );
    } else if (ctx && typeof ctx === 'object') {
      // Injection ran but both records were null: the read side works — the
      // WRITE side has no data for our ids (redirect service REDIS_URL /
      // NODE_ENV prefix / ids not forwarded), or the 150ms SSR read timed out.
      console.warn(
        '[BRIJ_CTX] source=INJECTED-EMPTY — SSR injection ran but Redis had ' +
          'no device/session records for our ids (check the writer: redirect ' +
          'service REDIS_URL / NODE_ENV key prefix). Using cookie fallback.'
      );
    } else if (ctx) {
      console.error(
        '[BRIJ_CTX] source=COOKIE-FALLBACK — window.__BRIJ_CTX__ is malformed:',
        ctx
      );
    } else {
      console.log(
        '[BRIJ_CTX] source=NOT-INJECTED — page was not processed by ' +
          'consumer-app-microservice SSR (local dev server, CDN-cached HTML, ' +
          'or old build). Using cookie fallback.'
      );
    }
  }
  return ctx;
};

// Raw ip_geo field from the session record (or undefined if not present).
export const ctxGeo = (field: keyof IpGeo): string | undefined => {
  const value = readBrijContext()?.session?.ip_geo?.[field];
  const resolved = typeof value === 'string' ? value : undefined;
  if (brijDebugEnabled()) {
    console.log(
      '[BRIJ_CTX][read] ip_geo.%s = %s (%s)',
      field,
      resolved ?? '(none)',
      resolved !== undefined ? 'from Redis' : 'not in Redis -> cookie fallback'
    );
  }
  return resolved;
};

// Per-slug rebate id from the session record (or undefined).
export const ctxRebateId = (slug: string): string | undefined => {
  const resolved = readBrijContext()?.session?.rebate_ids?.[slug];
  if (brijDebugEnabled()) {
    console.log(
      '[BRIJ_CTX][read] rebate_ids[%s] = %s (%s)',
      slug,
      resolved ?? '(none)',
      resolved !== undefined ? 'from Redis' : 'not in Redis -> cookie fallback'
    );
  }
  return resolved;
};
